import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Folder, FileText, FolderPlus, Upload, Download, Trash2, Pencil,
  ChevronRight, Home, Loader2, Check, X,
} from "lucide-react";

const drive = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

const fmtSize = (b) => {
  if (!b) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i ? 1 : 0)} ${u[i]}`;
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export function DriveBrowser({ ownerId = null, emptyText = "This vault is empty. Create a folder or upload a file to get started." }) {
  const [parent, setParent] = useState(null);
  const [crumbs, setCrumbs] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newFolder, setNewFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async (pid = null) => {
    setLoading(true);
    try {
      const params = {};
      if (ownerId) params.owner_id = ownerId;
      if (pid) params.parent_id = pid;
      const { data } = await drive.get("/drive", { params });
      setItems(data.items || []);
      setCrumbs(data.breadcrumb || []);
      setParent(pid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load vault");
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { load(null); }, [load]);

  const createFolder = async () => {
    if (!folderName.trim()) return;
    setBusy(true);
    try {
      await drive.post("/drive/folder", { name: folderName.trim(), parent_id: parent, owner_id: ownerId || undefined });
      setFolderName(""); setNewFolder(false);
      await load(parent);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create folder"); }
    finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        if (parent) fd.append("parent_id", parent);
        if (ownerId) fd.append("owner_id", ownerId);
        await drive.post("/drive/upload", fd);
      }
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}`);
      await load(parent);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (it) => {
    try {
      const { data, headers } = await drive.get(`/drive/${it.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));
      const a = document.createElement("a");
      a.href = url; a.download = it.name; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const saveRename = async (it) => {
    if (!renameVal.trim() || renameVal === it.name) { setRenaming(null); return; }
    try {
      await drive.patch(`/drive/${it.id}`, { name: renameVal.trim() });
      setRenaming(null); await load(parent);
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete ${it.type === "folder" ? "folder and all its contents" : "file"} "${it.name}"?`)) return;
    try {
      await drive.delete(`/drive/${it.id}`);
      toast.success("Deleted");
      await load(parent);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const btn = "inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-white/15 text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40";

  return (
    <div data-testid="drive-browser">
      {/* Toolbar + breadcrumb */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 text-sm text-white/60 flex-wrap" data-testid="drive-breadcrumb">
          <button onClick={() => load(null)} className="inline-flex items-center gap-1 hover:text-white" data-testid="drive-crumb-root">
            <Home className="h-3.5 w-3.5" /> Vault
          </button>
          {crumbs.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-white/30" />
              <button onClick={() => load(c.id)} className="hover:text-white">{c.name}</button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button className={btn} data-testid="drive-new-folder-btn" disabled={busy} onClick={() => setNewFolder((v) => !v)}>
            <FolderPlus className="h-4 w-4" /> New folder
          </button>
          <button className={btn} data-testid="drive-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          </button>
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="drive-file-input" onChange={onUpload} />
        </div>
      </div>

      {newFolder && (
        <div className="flex items-center gap-2 mb-4" data-testid="drive-new-folder-row">
          <input autoFocus value={folderName} onChange={(e) => setFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createFolder()}
            placeholder="Folder name" data-testid="drive-folder-name-input"
            className="bg-black/40 border border-white/15 px-3 py-2 text-sm text-white/90 outline-none w-64" />
          <button className={btn} data-testid="drive-create-folder-btn" disabled={busy} onClick={createFolder}><Check className="h-4 w-4" /></button>
          <button className={btn} onClick={() => { setNewFolder(false); setFolderName(""); }}><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-white/40" data-testid="drive-loading"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-white/40 border border-dashed border-white/10" data-testid="drive-empty">{emptyText}</div>
      ) : (
        <div className="border-y border-white/10 divide-y divide-white/10" data-testid="drive-list">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 py-3 group" data-testid={`drive-item-${it.id}`}>
              <div className="text-[hsl(var(--gold))]">
                {it.type === "folder" ? <Folder className="h-5 w-5" /> : <FileText className="h-5 w-5 text-white/50" />}
              </div>
              <div className="flex-1 min-w-0">
                {renaming === it.id ? (
                  <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRename(it); if (e.key === "Escape") setRenaming(null); }}
                    onBlur={() => saveRename(it)} data-testid={`drive-rename-input-${it.id}`}
                    className="bg-black/40 border border-white/20 px-2 py-1 text-sm text-white/90 outline-none w-full max-w-sm" />
                ) : it.type === "folder" ? (
                  <button onClick={() => load(it.id)} className="text-left text-white/90 hover:text-white truncate block w-full" data-testid={`drive-open-${it.id}`}>{it.name}</button>
                ) : (
                  <span className="text-white/80 truncate block">{it.name}</span>
                )}
              </div>
              <div className="hidden sm:block text-xs text-white/40 w-20 text-right">{it.type === "file" ? fmtSize(it.size) : ""}</div>
              <div className="hidden md:block text-xs text-white/40 w-28 text-right">{fmtDate(it.updated_at)}</div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {it.type === "file" && (
                  <button onClick={() => download(it)} title="Download" data-testid={`drive-download-${it.id}`} className="p-2 text-white/50 hover:text-white"><Download className="h-4 w-4" /></button>
                )}
                <button onClick={() => { setRenaming(it.id); setRenameVal(it.name); }} title="Rename" data-testid={`drive-rename-${it.id}`} className="p-2 text-white/50 hover:text-white"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(it)} title="Delete" data-testid={`drive-delete-${it.id}`} className="p-2 text-white/50 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
